/**
 * CreateNodeModal (S1.1)
 *
 * Inline add-child (and add-root) modal for the taxonomy admin page.
 * Guard failures come back machine-readable (CODE_TAKEN / LEVEL_DISABLED /
 * DEPTH_EXCEEDED …) and are surfaced localized — ">5 blocked localized".
 */

import { useEffect, useMemo } from 'react';
import { App, Input, Modal, Typography, theme } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { buildBreadcrumb, useInlineCreateNode } from '@/hooks/useTaxonomy';
import type { ProductCategory } from '@/types/api/productCategory';

const { Text } = Typography;

const createNodeSchema = z.object({
  code: z.string().trim().min(1, 'taxonomy:validation.codeRequired'),
  nameEn: z.string().trim().min(1, 'taxonomy:validation.nameEnRequired'),
  nameZh: z.string().trim().min(1, 'taxonomy:validation.nameZhRequired'),
  netsuiteSegmentValueId: z.string().trim().optional(),
  comment: z.string().trim().optional(),
});

type CreateNodeFormData = z.infer<typeof createNodeSchema>;

export interface CreateNodeModalProps {
  open: boolean;
  /** null = create a top-level node. */
  parentId: string | null;
  nodes: ProductCategory[];
  onClose: () => void;
  /** Called after a successful create with the new node id. */
  onCreated: (newNodeId: string) => void;
}

const defaultValues: CreateNodeFormData = {
  code: '',
  nameEn: '',
  nameZh: '',
  netsuiteSegmentValueId: '',
  comment: '',
};

const CreateNodeModal = ({ open, parentId, nodes, onClose, onCreated }: CreateNodeModalProps) => {
  const { t, i18n } = useTranslation(['taxonomy', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();
  const isZh = i18n.language.startsWith('zh');

  const parentPath = useMemo(
    () => (parentId ? buildBreadcrumb(nodes, parentId, isZh ? 'zh' : 'en') : ''),
    [isZh, nodes, parentId]
  );

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setFocus,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<CreateNodeFormData>({
    resolver: zodResolver(createNodeSchema),
    defaultValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // Shared submit path (S3.2) — root-create + inline quick-create converge here.
  // The hook owns the createNode call and the CODE_TAKEN dup-guard UX
  // (setError('code') + setFocus('code')); this modal owns its own success /
  // non-CODE_TAKEN failure messaging.
  const { submit: runCreate } = useInlineCreateNode<CreateNodeFormData>({ setError, setFocus });

  useEffect(() => {
    if (open) reset(defaultValues);
  }, [open, reset]);

  const submit = async (data: CreateNodeFormData) => {
    const result = await runCreate({
      code: data.code.trim(),
      nameEn: data.nameEn.trim(),
      nameZh: data.nameZh.trim(),
      parentId,
      netsuiteSegmentValueId: data.netsuiteSegmentValueId?.trim() || undefined,
      comment: data.comment?.trim() || undefined,
    });
    if (result.ok) {
      message.success(t('taxonomy:create.success'));
      onClose();
      onCreated(result.id);
      return;
    }
    if (result.guard !== 'CODE_TAKEN') {
      // CODE_TAKEN is already surfaced on the code field by the hook.
      message.error(
        result.guard ? t(`taxonomy:errors.${result.guard}`) : t('common:messages.createFailed')
      );
    }
  };

  const fieldError = (name: keyof CreateNodeFormData) =>
    errors[name] && (touchedFields[name] || errors[name]?.type === 'server') ? (
      <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
    ) : null;

  const requiredMark = <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>;

  return (
    <Modal
      open={open}
      title={t('taxonomy:create.title')}
      okText={t('common:actions.add')}
      cancelText={t('common:actions.cancel')}
      onCancel={onClose}
      onOk={handleSubmit(submit)}
      confirmLoading={isSubmitting}
      width={520}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      destroyOnHidden
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
        {parentId
          ? t('taxonomy:create.under', { path: parentPath })
          : t('taxonomy:create.rootHint')}
      </Text>

      <div style={formStyles.fieldContainerStyle}>
        <Text style={formStyles.labelStyle}>
          {requiredMark}
          {t('taxonomy:fields.code')}
        </Text>
        <Controller
          name="code"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              size="large"
              placeholder={t('taxonomy:create.codePlaceholder')}
              style={formStyles.inputStyle}
              status={errors.code ? 'error' : undefined}
            />
          )}
        />
        {fieldError('code')}
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: '4px', fontSize: token.fontSizeSM }}
        >
          {t('taxonomy:create.codeHint')}
        </Text>

        <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
          {requiredMark}
          {t('taxonomy:fields.nameEn')}
        </Text>
        <Controller
          name="nameEn"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              size="large"
              style={formStyles.inputStyle}
              status={errors.nameEn ? 'error' : undefined}
            />
          )}
        />
        {fieldError('nameEn')}

        <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
          {requiredMark}
          {t('taxonomy:fields.nameZh')}
        </Text>
        <Controller
          name="nameZh"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              size="large"
              style={formStyles.inputStyle}
              status={errors.nameZh ? 'error' : undefined}
            />
          )}
        />
        {fieldError('nameZh')}

        <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
          {t('taxonomy:fields.netsuiteRef')}
        </Text>
        <Controller
          name="netsuiteSegmentValueId"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              size="large"
              placeholder={t('taxonomy:detail.netsuitePlaceholder')}
              style={formStyles.inputStyle}
            />
          )}
        />

        <Text style={{ ...formStyles.labelStyle, marginTop: '16px' }}>
          {t('taxonomy:fields.comment')}
        </Text>
        <Controller
          name="comment"
          control={control}
          render={({ field }) => (
            <Input.TextArea
              {...field}
              rows={2}
              placeholder={t('taxonomy:detail.commentPlaceholder')}
              style={{ ...formStyles.inputStyle, height: 'auto' }}
            />
          )}
        />
      </div>
    </Modal>
  );
};

export default CreateNodeModal;
